import {useMemo} from 'react';
import {view, Icon} from '@sail/ui';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {SelectionCard} from 'src/v2/components/common/SelectionCard';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {
  NETWORK_TIMING,
  DEFAULT_TIMING,
  formatTimingRange,
  type UsdcNetwork,
} from 'src/v2/components/anyCrypto/networkTiming';
import {getNetworkIcon, getNetworkName} from 'src/components/Wallet/constants';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';

import {message, useIntl} from '@sail/intl';

const messages = {
  selectNetwork: message<{
    token: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.network_select.select_network',
    description: 'Label for network selection list',
    defaultMessage: 'Select network for {token}',
  }),
};

export const NetworkSelectScreen = () => {
  const intl = useIntl();
  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();
  const selectedToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);

  const networkList = useMemo(() => {
    const networks = [
      ...new Set(
        supportedCurrencies
          .filter((c) => c.assetCode === selectedToken)
          .map((c) => c.currencyNetwork),
      ),
    ];
    return networks.filter((n): n is UsdcNetwork => n !== 'bitcoin');
  }, [supportedCurrencies, selectedToken]);

  const handleNetworkSelect = (network: UsdcNetwork) => {
    setValue((state) => {
      state.anyCryptoDeposit.selectedNetwork = network;
      state.anyCryptoDeposit.quote = {
        depositAddress: null,
        inputAmount: null,
        inputChain: null,
        inputCurrency: null,
      };
      state.screen = 'ANY_CRYPTO_REFUND';
    });
  };

  return (
    <ScreenContentLayout
      onBack={() =>
        setValue((state) => {
          state.screen = isAnyCryptoWalletConnectFlow
            ? 'WALLET_SELECT_GRID'
            : 'TOKEN_SELECT';
        })
      }
    >
      <view.div css={{stack: 'y', gap: 'small'}}>
        <Text type="bodyEmphasized" css={{color: 'text'}}>
          {intl.formatMessage(messages.selectNetwork, {
            token: formatAssetCode(selectedToken),
          })}
        </Text>
        <view.div css={{stack: 'y', gap: 'small'}} role="listbox">
          {networkList.map((network) => (
            <SelectionCard
              key={network}
              icon={
                <Icon
                  icon={getNetworkIcon(network)}
                  css={{
                    width: 'xlarge',
                    height: 'xlarge',
                    borderRadius: 'xsmall',
                  }}
                />
              }
              label={getNetworkName(network)}
              accessory={formatTimingRange(
                (NETWORK_TIMING[network] ?? DEFAULT_TIMING).processingTime,
                intl,
              )}
              onSelect={() => handleNetworkSelect(network)}
            />
          ))}
        </view.div>
      </view.div>
    </ScreenContentLayout>
  );
};
